import { mapActions, mapState } from 'pinia';
import { flatMap, merge } from 'lodash-es';
import * as ImmersiveReader from '@microsoft/immersive-reader-sdk';
import { encodeHTML, formatPageTitle } from '@/lib/utils';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'currentPageIndex',
      'draftType',
      'getOrderedModules',
    ]),
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.SET_EDITOR_STATUS_MESSAGE,
    ]),
    getDomModulesArray() {
      /*
        Get all module vue-grid-item DOM elements on current page.
        Exclude the placeholder and preview module.
      */
      const domModules = document.querySelectorAll('.editor__stage .vue-grid-item:not(.vue-grid-placeholder):not([id="00000000"]');
      // domModules is originally an HTMLCollection, transform into an array to use array methods.
      const domModulesArray = Array.from(domModules);
      return domModulesArray;
    },
    /**
     * Process html from DOM elements into chunks to be fed into Immersive Reader
     * @param {array} orderedMods - Ordered modules from Pinia
     * @param {array} domModules - Modules from the DOM
     */
    getOrderedChunks(orderedMods, domModules) {
      // Index the ids of immersive reader applicable modules
      const modKeys = orderedMods
        .filter((mod) => mod.type === 'text'
                || mod.type === 'prompt'
                || mod.type === 'single_selection'
                || mod.type === 'multiple_selection'
                || mod.type === 'block'
                || mod.type === 'asset'
                || mod.type === 'upload')
        .map((mod) => mod.id);

      // Filter only appropriate modules (see above)
      let orderedChunks = domModules.filter((mod) => modKeys.includes(mod.id.replace('module-', '')));

      /** Because certain modules do not have a 1:1 relationship of chunks vs module
       * (a module can have multiple chunks), we use a flatMap instead of a normal map.
       * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flatMap#For_adding_and_removing_items_during_a_map
       */
      orderedChunks = flatMap(orderedChunks, (mod) => {
        let element;

        // eslint-disable-next-line no-cond-assign
        if (element = mod.querySelector('.short-answer, .single-selection-module, .multiple-selection-module, .block-module')) { // Interactive module
          const instructions = mod.querySelector('.short-answer__instructions, .single-selection-module__instructions, .multiple-selection-module__instructions, .block-module__instructions');

          const blocks = [{
            content: instructions.outerHTML,
            mimeType: 'text/html',
          }];

          if (this.mode === 'EDIT') {
            // Wrap prompt text in html for Immersive Reader. This needs to be escaped
            // since it hasn't been sanitized by the backend
            const prompt = document.createElement('p');
            prompt.innerHTML = encodeHTML(element.querySelector('textarea').value);
            blocks.push({
              content: prompt.outerHTML,
              mimeType: 'text/html',
            });
          }

          return blocks;
        }

        // eslint-disable-next-line no-cond-assign
        if ((element = mod.querySelector('.ProseMirror')) || (element = mod.querySelector('.nebula-card__content'))) { // Text editor or asset module
          return [{
            content: element.innerHTML,
            mimeType: 'text/html',
          }];
        }

        return [];
      });

      return orderedChunks;
    },
    closeImmersiveReader() {
      ImmersiveReader.close();
    },
    async launchImmersiveReader(launchOptions = {}) {
      const noTokenError = this.$t('No token was supplied for the Immersive Reader');

      let tokenResult;
      try {
        tokenResult = await this.$deApi.get('/reader/token');
        if (tokenResult.status !== 200 || !tokenResult.data?.token) {
          throw new Error(tokenResult.message || noTokenError);
        }
      } catch (error) {
        this[types.SET_EDITOR_STATUS_MESSAGE]({ message: 'editor-error', error: error.message });
        throw error;
      }

      const { token, subdomain } = tokenResult.data;
      const domModules = this.getDomModulesArray();
      const chunks = this.getOrderedChunks(this.getOrderedModules, domModules);
      const data = {
        title: formatPageTitle(this.currentPage, this.currentPageIndex, this.draftType),
        chunks,
      };

      // More information about Immersive Reader options:
      // https://docs.microsoft.com/en-us/azure/applied-ai-services/immersive-reader/reference
      const options = merge({
        cookiePolicy: ImmersiveReader.CookiePolicy.Enable,
        uiZIndex: 2000,
        onExit: () => this.$emit('exit'),
      }, launchOptions);

      return ImmersiveReader.launchAsync(
        token,
        subdomain,
        data,
        options,
      ).then((launchResponse) => {
        this.$emit('launch');
        return launchResponse;
      }).catch((error) => {
        const message = error.code === 'BadArgument'
          ? this.$t('There is no valid text to read')
          : error.message;
        this[types.SET_EDITOR_STATUS_MESSAGE]({ message: 'editor-error', error: message });
        throw error;
      });
    },
  },
};
